import type { ActionsNetwork } from 'shared/store/actions'
import { actionNetworkError } from 'shared/store/actions'
import type { State as HomeState } from 'shared/store/homepage'
import { ws } from 'shared/store/ws'
import endpoints from 'shared/store/endpoints'
import type { Dispatch } from 'redux'
import { errors } from 'shared/utils'
import type { OperationDevice, OperationRepeat } from 'shared/types'
import type {
    CouponSubscribe,
    OperationCouponData
} from 'shared/store/custom-operation'
import type { RootState } from 'shared/store/reducers'

export const FETCH_OPERATIONS_REPEAT_QUERY = 'fetch-operations-repeat-query'

type GetState = () => RootState
const root = endpoints.pages.operation.root

type ActionGenericOperationPageDataResponse = {
    type: 'PAGE_DATA_GENERIC_OPERATION_RESPONSE',
    data: OperationDevice
}
export type ActionOperationRepeatSwapCode = {
    type: 'OPERATION_REPEAT_SWAP_CODE',
    operationsRepeat: Array<OperationDevice>
}

export type ActionOperationRepeatDriveCode = {
    type: 'OPERATION_REPEAT_DRIVE_CODE',
    operationsRepeat: Array<OperationDevice>
}

type ActionGenericOperationError = {
    type: 'GENERIC_OPERATION_ERROR',
    error: boolean
}

type ActionHomepageOperationRepeat = {
    type: 'PAGE_DATA_HOMEPAGE_RESPONSE',
    data: HomeState
}

type ActionSetGenericOperationRepeat = {
    type: 'GENERIC_SET_OPERATION_REPEAT',
    operationRepeat: OperationRepeat
}

const setGenericOperationRepeat = (
    operationRepeat: OperationRepeat
): ActionSetGenericOperationRepeat => ({
    type: 'GENERIC_SET_OPERATION_REPEAT',
    operationRepeat
})

const fetchGenericOperationPageData = (subscribe: CouponSubscribe) => async (
    dispatch: Dispatch<any>
) => {
    const { operationCode, restaurantCode, deviceCheckPayload } = subscribe
    const { king, hash } = deviceCheckPayload
    const fetch = `${root}/${operationCode}`
    try {
        let response = null
        if (restaurantCode) {
            response = await ws.get(`${fetch}/drive`, {
                params: {
                    king,
                    restaurantCode,
                    hash
                }
            })
        } else {
            response = await ws.post(`${fetch}/coupons`, deviceCheckPayload)
        }
        dispatch(getGenericOperationData(response.data))
    } catch (error) {
        console.error(
            'Error, could not get coupons for generic operation :',
            error
        )
        manageError(error, dispatch)
    }
}

const getOperationRepeatDriveCode = (
    subscribe: OperationCouponData,
    queryClient: any
) => async (dispatch: Dispatch<any>, getState: GetState) => {
    const { operationCode, restaurantCode, deviceCheckPayload } = subscribe
    const wsEndpoint = `${root}/${operationCode}/drive?restaurantCode=${restaurantCode}`
    const { operationsRepeat } = getState().ecommerceCatalogState

    try {
        //$FlowFixMe
        const response = await ws.post(wsEndpoint, deviceCheckPayload)
        if (operationsRepeat) {
            const index = operationsRepeat.findIndex(
                e =>
                    e.coupons &&
                    !!e.coupons.find(c => c.restaurantCode === restaurantCode)
            )
            let index2 = -1
            if (operationsRepeat[index] && operationsRepeat[index].coupons) {
                index2 = operationsRepeat[index].coupons.findIndex(
                    c => c && c.restaurantCode === restaurantCode
                )
            }
            let newOperationsRepeat = [...operationsRepeat]
            if (
                index >= 0 &&
                index2 >= 0 &&
                newOperationsRepeat[index] &&
                newOperationsRepeat[index].coupons
            ) {
                newOperationsRepeat[index].coupons[index2] = response.data
            }
            dispatch(actionGetOperationRepeatDriveCode(newOperationsRepeat))
        }

        queryClient.setQueriesData(
            {
                queryKey: [FETCH_OPERATIONS_REPEAT_QUERY]
            },
            (oldData?: OperationDevice[]) => {
                if (!oldData) {
                    return undefined
                }

                return oldData.map(
                    (oldOperation: OperationDevice): OperationDevice => {
                        if (oldOperation.code === operationCode) {
                            return {
                                ...oldOperation,
                                coupons: (oldOperation.coupons ?? []).map(
                                    coupon => {
                                        if (
                                            coupon.restaurantCode ===
                                            restaurantCode
                                        ) {
                                            return response.data
                                        }

                                        return coupon
                                    }
                                )
                            }
                        }
                        return oldOperation
                    }
                )
            }
        )
        return response
    } catch (error) {
        console.error(
            'Error, could not get drive code for operation repeat:',
            error
        )
        manageError(error, dispatch)
    }
}

const updateCouponInOperations = (
    oldData?: OperationDevice[],
    couponCode: string,
    confirmChoiceResponseOperation: OperationDevice
): OperationDevice[] => {
    if (!oldData) {
        return []
    }
    return oldData.map((oldOperation: OperationDevice): OperationDevice => {
        if (oldOperation.code === confirmChoiceResponseOperation.code) {
            return {
                ...oldOperation,
                coupons: (oldOperation.coupons ?? []).map(coupon => {
                    if (!confirmChoiceResponseOperation.coupons) {
                        return coupon
                    }

                    if (coupon.restaurantCode === couponCode) {
                        return confirmChoiceResponseOperation.coupons[0]
                    }

                    return coupon
                })
            }
        }
        return oldOperation
    })
}

const swapOperationRepeatCode = (subscribe: OperationCouponData) => async (
    dispatch: Dispatch<any>,
    getState: GetState
) => {
    const { operationCode, restaurantCode, deviceCheckPayload } = subscribe
    const wsEndpoint = `${root}/${operationCode}/swap?restaurantCode=${restaurantCode}`
    const { operationsRepeat } = getState().ecommerceCatalogState

    try {
        //$FlowFixMe
        const response = await ws.post(wsEndpoint, deviceCheckPayload)
        if (operationsRepeat) {
            const newOperationsRepeat = operationsRepeat.reduce(
                (acc, operation) => {
                    if (operation.code !== operationCode) {
                        return [...acc, operation]
                    }

                    const coupons = operation.coupons
                        ? operation.coupons.map(coupon => {
                              if (coupon.restaurantCode === restaurantCode) {
                                  return response.data?.coupons[0]
                              } else {
                                  return coupon
                              }
                          })
                        : []

                    return [...acc, { ...operation, coupons }]
                },
                []
            )

            dispatch(actionSwapOperationRepeatCode(newOperationsRepeat))
            return response.data
        }
    } catch (error) {
        if (error?.response?.status === 403) {
            throw new Error('operation.mystery.burger.options.cant-change')
        }
        console.error('Error, could not get code for operation repeat:', error)
        manageError(error, dispatch)
    }
}

const getWallet = (
    operationCode: string,
    restaurantCode: string,
    king: string
) => async (dispatch: Dispatch<any>) => {
    try {
        const response = await ws.get(`${root}/${operationCode}/wallet`, {
            params: {
                king,
                restaurantCode
            }
        })
        dispatch(actionError(undefined))
        return response.data && response.data.wallet
    } catch (error) {
        console.error('Error, could not get coupon for wallet')
        manageError(error, dispatch)
        return false
    }
}

const getGenericOperationData = (
    data: OperationDevice
): ActionGenericOperationPageDataResponse => ({
    type: 'PAGE_DATA_GENERIC_OPERATION_RESPONSE',
    data
})

const actionGetOperationRepeatDriveCode = (
    operationsRepeat: Array<OperationDevice>
): ActionOperationRepeatDriveCode => ({
    type: 'OPERATION_REPEAT_DRIVE_CODE',
    operationsRepeat
})

const actionSwapOperationRepeatCode = (
    operationsRepeat: Array<OperationDevice>
): ActionOperationRepeatSwapCode => ({
    type: 'OPERATION_REPEAT_SWAP_CODE',
    operationsRepeat
})

const actionError = (error: any): ActionGenericOperationError => ({
    type: 'GENERIC_OPERATION_ERROR',
    error
})

const manageError = (error: any, dispatch: Dispatch<any>) => {
    console.error('Error in generic operation', error)
    switch (error) {
        case errors.NETWORK:
            dispatch(
                actionNetworkError({
                    type: errors.NETWORK
                })
            )
            break
        case errors.TIMEOUT:
            dispatch(
                actionNetworkError({
                    type: errors.TIMEOUT
                })
            )
            break
        default:
            dispatch(actionError(error))
            break
    }
}

export {
    fetchGenericOperationPageData,
    getWallet,
    actionError,
    setGenericOperationRepeat,
    getOperationRepeatDriveCode,
    swapOperationRepeatCode,
    updateCouponInOperations,
    actionSwapOperationRepeatCode
}

export type Action =
    | ActionGenericOperationPageDataResponse
    | ActionHomepageOperationRepeat
    | ActionGenericOperationError
    | ActionSetGenericOperationRepeat
    | ActionOperationRepeatDriveCode
    | ActionsNetwork
    | ActionOperationRepeatSwapCode
